import { type ChatMessage, type QueravelConversation, queravelChatOf } from "@rhema/client";
import { MessageBubble } from "@rhema/ui";
import type { ReactNode } from "react";
import { useLinkStatus } from "../links/useLinks";
import { useMenus } from "../menus/actions";
import { WithMenu } from "../menus/WithMenu";
import { QueravelAnswer } from "./QueravelParts";

/** Whether Queravel is offered at all: the server has connected apps on and knows Queravel. */
export function useQueravelOffered(): boolean {
  const { status, enabled } = useLinkStatus("queravel");
  return enabled === true && status !== null;
}

/**
 * The stored turns of one Queravel conversation, as they appear in the panel. `actions` adds the
 * buttons under each answer. An answer's menu acts on that answer only: Copy answer, and Use as
 * reply when `onReply` is given. Renaming and deleting stay on the history row.
 */
export function StoredTurns({
  messages,
  conversation,
  actions,
  onReply,
}: {
  messages: ChatMessage[];
  conversation: QueravelConversation | undefined;
  actions?: (answer: string) => ReactNode;
  onReply?: (answer: string) => void;
}) {
  const menus = useMenus();
  const items: ReactNode[] = [];
  if (!conversation) return null;
  const menuOf = (answer: string) => () =>
    menus.queravelAnswer(answer, onReply ? () => onReply(answer) : undefined);
  for (const m of messages) {
    const c = m.content;
    if (!c || m.deleted || m.kind !== "message" || queravelChatOf(m) !== conversation.id) continue;
    if (c.type === "queravel") {
      items.push(
        <WithMenu key={m.id} as="div" label="this answer" items={menuOf(c.body)} more={false}>
          <QueravelAnswer
            text={c.body}
            references={c.references}
            artifacts={c.artifacts ?? []}
            tools={c.tools ?? []}
          >
            {actions?.(c.body)}
          </QueravelAnswer>
        </WithMenu>,
      );
    } else if (c.type === "text") {
      items.push(
        <div key={m.id} className="flex justify-end">
          <MessageBubble direction="out" time="" hideMeta className="max-w-[82%]">
            {c.queravel?.label ?? m.edited ?? c.body}
          </MessageBubble>
        </div>,
      );
    }
  }
  return <>{items}</>;
}
